/** Renders one field from the template snapshot. Used by the public form
 *  page and (read-only) by the admin submission detail view.
 */
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export function IntakeField({ field, value, onChange, readOnly = false, error, uploadEndpoint, downloadEndpoint, ocrStatusEndpoint, onAcceptOcrSuggestion }) {
  const id = `if-${field.id}`;
  const labelEl = (
    <Label htmlFor={id} className="block text-sm font-medium text-zinc-900">
      {field.label}
      {field.required && <span className="text-red-600 ml-1">*</span>}
    </Label>
  );

  const wrap = (control) => (
    <div className="space-y-1.5" data-testid={`intake-field-${field.id}`}>
      {field.type !== "checkbox" && labelEl}
      {control}
      {field.help && field.type !== "checkbox" && (
        <p className="text-xs text-zinc-500">{field.help}</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );

  if (readOnly) {
    if (field.type === "checkbox") {
      return (
        <div className="flex items-start gap-2 py-1">
          <Checkbox checked={!!value} disabled />
          <div className="text-sm text-zinc-700">
            {field.label}
            {field.help && (
              <p className="text-xs text-zinc-500 mt-1">{field.help}</p>
            )}
          </div>
        </div>
      );
    }
    // Likert (e.g. PDQ-39) — pretty-print the label + numeric value so the
    // operator sees "Sometimes (2)" instead of a raw "2".
    if (field.type === "likert") {
      const opt = (field.options || []).find(
        (o) => o.value === value || Number(o.value) === Number(value),
      );
      // Suppress the "(value)" subhead when it matches the label — a
      // Yes/No likert otherwise renders "Yes (Yes)".
      const resolvedLabel = opt ? opt.label : String(value);
      const sameAsLabel =
        String(value).trim().toLowerCase() === String(resolvedLabel).trim().toLowerCase();
      const showValue = !field.hide_option_values && !sameAsLabel;
      return wrap(
        <div className="px-3 py-2 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-900 min-h-[36px]">
          {value === undefined || value === null || value === ""
            ? <span className="text-zinc-400">— not answered —</span>
            : (
              <span>
                {resolvedLabel}
                {showValue && (
                  <span className="ml-2 text-xs text-zinc-500 font-mono-num">
                    ({value})
                  </span>
                )}
              </span>
            )}
        </div>,
      );
    }
    return wrap(
      <div className="px-3 py-2 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-900 whitespace-pre-wrap min-h-[36px]">
        {value === undefined || value === "" || value === null
          ? <span className="text-zinc-400">—</span>
          : String(value)}
      </div>,
    );
  }

  switch (field.type) {
    case "long-text":
      return wrap(
        <Textarea
          id={id}
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "date":
      return wrap(
        <Input
          id={id}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "number":
      return wrap(
        <Input
          id={id}
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "email":
      return wrap(
        <Input
          id={id}
          type="email"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "phone":
      return wrap(
        <Input
          id={id}
          type="tel"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "checkbox":
      return (
        <label
          className="flex items-start gap-3 cursor-pointer select-none py-1"
          data-testid={`intake-field-${field.id}`}
        >
          <Checkbox
            checked={!!value}
            onCheckedChange={(v) => onChange(!!v)}
            data-testid={`intake-input-${field.id}`}
          />
          <div>
            <span className="text-sm text-zinc-900">
              {field.label}
              {field.required && <span className="text-red-600 ml-1">*</span>}
            </span>
            {field.help && (
              <p className="mt-1 text-xs text-zinc-600">{field.help}</p>
            )}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        </label>
      );
    case "signature":
      return wrap(
        <SignatureInput
          value={value}
          onChange={onChange}
          testId={`intake-input-${field.id}`}
        />,
      );
    case "rating":
      return wrap(
        <div className="space-y-1.5" data-testid={`intake-input-${field.id}`}>
          <input
            id={id}
            type="range"
            min="0"
            max="10"
            step="1"
            value={value === "" || value === undefined || value === null ? "0" : value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full accent-zinc-900"
          />
          <div className="flex justify-between text-2xs text-zinc-500 font-mono-num">
            <span>0<br/><span className="normal-case tracking-normal">{field.min_label || "none"}</span></span>
            <span>5</span>
            <span>10<br/><span className="normal-case tracking-normal">{field.max_label || "worst"}</span></span>
          </div>
          <div className="text-center text-sm font-mono-num text-zinc-950">
            {value === "" || value === undefined || value === null ? 0 : value} / 10
          </div>
        </div>,
      );
    case "likert":
      // Horizontal radio-button scale (used by PDQ-39 and any future Likert
      // questionnaires). `field.options` is a list of {label, value:number}.
      // Storing the numeric value directly makes auto-scoring trivial on
      // the backend.
      return wrap(
        <div
          className="flex flex-wrap gap-2 sm:gap-3"
          data-testid={`intake-input-${field.id}`}
          role="radiogroup"
          aria-labelledby={id}
        >
          {(field.options || []).map((opt) => {
            const checked = value === opt.value || Number(value) === Number(opt.value);
            // Suppress the numeric subhead when it's identical to the
            // label (e.g. Yes/No likerts), otherwise the button renders
            // "Yes" stacked on top of "Yes".
            const sameAsLabel =
              String(opt.value).trim().toLowerCase() === String(opt.label).trim().toLowerCase();
            const showValue = !field.hide_option_values && !sameAsLabel;
            return (
              <label
                key={opt.value}
                className={`flex-1 min-w-[110px] cursor-pointer rounded-lg border px-3 py-2 text-xs sm:text-sm text-center transition-colors select-none ${
                  checked
                    ? "border-zinc-900 bg-zinc-900 text-white"
                    : "border-zinc-200 hover:border-zinc-400 bg-white"
                }`}
                data-testid={`intake-input-${field.id}-opt-${opt.value}`}
              >
                <input
                  type="radio"
                  name={id}
                  value={opt.value}
                  checked={checked}
                  onChange={() => onChange(opt.value)}
                  className="sr-only"
                />
                {showValue && (
                  <div className="font-mono-num text-2xs opacity-70 mb-0.5">
                    {opt.value}
                  </div>
                )}
                <div className="leading-tight">{opt.label}</div>
              </label>
            );
          })}
        </div>,
      );
    case "text":
    default:
      return wrap(
        <Input
          id={id}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`intake-input-${field.id}`}
        />,
      );
    case "radio":
      // Vertical single-choice list — used by referral-source /
      // hear-about-us pickers where a horizontal chip grid would
      // wrap awkwardly across 10+ options.
      return wrap(
        <div
          className="flex flex-col gap-1.5"
          data-testid={`intake-input-${field.id}`}
          role="radiogroup"
          aria-labelledby={id}
        >
          {(field.options || []).map((opt) => {
            const checked = String(value) === String(opt.value);
            return (
              <label
                key={opt.value}
                className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm transition-colors ${
                  checked
                    ? "border-zinc-900 bg-zinc-900 text-white"
                    : "border-zinc-200 hover:border-zinc-400 bg-white"
                }`}
                data-testid={`intake-input-${field.id}-opt-${opt.value}`}
              >
                <input
                  type="radio"
                  name={id}
                  value={opt.value}
                  checked={checked}
                  onChange={() => onChange(opt.value)}
                  className={checked ? "accent-white" : "accent-zinc-900"}
                />
                <span className="leading-tight">{opt.label}</span>
              </label>
            );
          })}
        </div>,
      );
    case "select":
      // Native <select> — cheap fallback for form-authors who want a
      // dropdown rather than a full vertical list. Kept alongside
      // "radio" so backend ALLOWED_FIELD_TYPES round-trips cleanly.
      return wrap(
        <select
          id={id}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/20"
          data-testid={`intake-input-${field.id}`}
        >
          <option value="">Select an option…</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>,
      );
    case "file":
      // Value shape: {storage_path, filename, content_type, size, uploaded_at}
      // Uploads happen inline (POST multipart to the token-scoped
      // /public/intake/{token}/upload/{field_id}) before the client
      // hits Submit — the metadata dict returned by the server is what
      // gets persisted as the answer.
      return wrap(
        <FileInput
          field={field}
          value={value}
          onChange={onChange}
          uploadEndpoint={uploadEndpoint}
          downloadEndpoint={downloadEndpoint}
          ocrStatusEndpoint={ocrStatusEndpoint}
          onAcceptOcrSuggestion={onAcceptOcrSuggestion}
          readOnly={readOnly}
          testId={`intake-input-${field.id}`}
        />,
      );
  }
}

function FileInput({ field, value, onChange, uploadEndpoint, downloadEndpoint, ocrStatusEndpoint, onAcceptOcrSuggestion, readOnly, testId }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [progress, setProgress] = useState(0);
  // OCR pipeline status: null (idle) | "pending" | "done" | "failed" |
  // "dismissed" (client saw the suggestion and clicked Ignore).
  const [ocr, setOcr] = useState(null);
  const attached = value && typeof value === "object" && value.storage_path;

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting same file after clear
    if (!file || !uploadEndpoint) return;
    setBusy(true); setErr(null); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // XHR gives us upload progress; fetch does not.
      const meta = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", uploadEndpoint);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error("Bad server response")); }
          } else {
            let detail = xhr.responseText;
            try { detail = JSON.parse(xhr.responseText).detail || detail; } catch {}
            reject(new Error(detail || `Upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(fd);
      });
      onChange(meta);
    } catch (ex) {
      setErr(ex.message || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
    }
  }

  // Poll the OCR status endpoint every 2s while a file is attached and
  // the client hasn't dismissed the suggestion yet. Stops as soon as
  // the backend returns done/failed. Keeps `ocr` in state so the UI
  // reflects pending/error/suggestion states without a manual refresh.
  useEffect(() => {
    if (!attached || !ocrStatusEndpoint) return;
    if (ocr?.status === "dismissed" || ocr?.status === "done" || ocr?.status === "failed") return;
    let dead = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await fetch(ocrStatusEndpoint, { credentials: "omit" });
        if (dead) return;
        if (r.ok) {
          const body = await r.json();
          setOcr(body);
          if (body.status === "done" || body.status === "failed") return;
        }
      } catch { /* transient — keep polling */ }
      // Cap at ~90s of polling — beyond that the OCR is almost
      // certainly stuck.
      if (!dead && tries < 45) setTimeout(tick, 2000);
    };
    tick();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attached, ocrStatusEndpoint, ocr?.status]);

  return (
    <div className="space-y-2" data-testid={testId}>
      {attached ? (
        <div className="flex items-center justify-between gap-2 p-2 rounded border border-emerald-300 bg-emerald-50 text-sm text-emerald-900">
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate" title={value.filename}>
              ✓ {value.filename}
            </div>
            <div className="text-2xs opacity-70">
              {value.content_type} · {(value.size / 1024).toFixed(0)} KB
            </div>
          </div>
          {downloadEndpoint && (
            <a href={downloadEndpoint}
               target="_blank" rel="noreferrer"
               className="text-xs underline text-emerald-800 hover:text-emerald-900 whitespace-nowrap"
               data-testid={`${testId}-download`}>
              open
            </a>
          )}
          {!readOnly && (
            <button type="button"
                    className="text-xs underline text-emerald-800 hover:text-emerald-900"
                    onClick={() => onChange(null)}
                    data-testid={`${testId}-remove`}>
              replace
            </button>
          )}
        </div>
      ) : (
        !readOnly && (
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <span className="px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-900">
              {busy ? `Uploading ${progress}%…` : "Choose file"}
            </span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={pick}
              disabled={busy || !uploadEndpoint}
              data-testid={`${testId}-picker`}
            />
            {!uploadEndpoint && (
              <span className="text-xs text-zinc-500">(upload disabled)</span>
            )}
          </label>
        )
      )}
      {err && <p className="text-xs text-red-700" data-testid={`${testId}-error`}>{err}</p>}
      {/* AI-extracted-values suggestion. Only surfaces on the public
          form (where `onAcceptOcrSuggestion` is provided) and only
          when the OCR pipeline actually produced a summary line. */}
      {attached && onAcceptOcrSuggestion && ocr?.status === "pending" && (
        <div className="text-2xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1.5 flex items-center gap-2"
             data-testid={`${testId}-ocr-pending`}>
          <span className="inline-block h-2 w-2 rounded-full bg-sky-500 animate-pulse"></span>
          Reading your report… we'll try to pre-fill your T-Scores if we can.
        </div>
      )}
      {attached && onAcceptOcrSuggestion && ocr?.status === "done" && ocr.suggestion && (
        <div className="rounded-md border border-indigo-300 bg-indigo-50 text-indigo-900 p-2 text-xs space-y-1.5"
             data-testid={`${testId}-ocr-suggestion`}>
          <div className="font-semibold flex items-center gap-1">
            <span>✨ We found this in your report</span>
            <span className="opacity-60 font-normal">(please confirm below)</span>
          </div>
          <div className="font-mono-num bg-white/70 border border-indigo-200 rounded px-2 py-1 leading-snug">
            {ocr.suggestion}
          </div>
          <div className="flex gap-2 pt-0.5">
            <button type="button"
                    className="px-2 py-0.5 rounded bg-indigo-700 hover:bg-indigo-800 text-white text-2xs font-semibold"
                    onClick={() => {
                      onAcceptOcrSuggestion(ocr.suggestion);
                      setOcr({ ...ocr, status: "dismissed" });
                    }}
                    data-testid={`${testId}-ocr-accept`}>
              Fill for me
            </button>
            <button type="button"
                    className="px-2 py-0.5 rounded border border-indigo-300 hover:bg-indigo-100 text-2xs"
                    onClick={() => setOcr({ ...ocr, status: "dismissed" })}
                    data-testid={`${testId}-ocr-dismiss`}>
              Ignore
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SignatureInput({ value, onChange, testId }) {
  const [touched, setTouched] = useState(false);
  return (
    <div className="space-y-2">
      <Input
        value={value || ""}
        onChange={(e) => { setTouched(true); onChange(e.target.value); }}
        placeholder="Type your full name as a signature"
        className="italic font-serif text-base"
        data-testid={testId}
      />
      <p className="text-xs text-zinc-500">
        Typing your name above acts as your electronic signature.
        {touched && value && <span className="ml-1 text-emerald-700">✓ signed</span>}
      </p>
    </div>
  );
}
